import React, { useEffect } from 'react'

import clsx from 'clsx'
import Masonry from 'react-masonry-css'

import ArticleCard from 'components/ArticleCard'
import Button from 'components/Button'
import Grid from 'components/Grid'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'
import { ROUTE_NAMES } from 'routes'
import routeService from 'services/routes'

import useArticlesContext from '../../ArticlesProvider'
import { DEFAULT_TOPIC, SEE_ALL_TOPIC } from '../../constants'
import ArticleCardSkeleton from '../ArticleCardSkeleton'
import TopicChip from '../TopicChip'
import TopicsCarousel from '../TopicsCarousel'

import classes from './FilterByTopic.module.scss'

const MASONRY_COLUMNS = {
  xs: 1,
  sm: 2,
}

/** @type {import("./types").default} */
const FilterByTopic = ({ topics }) => {
  const { is, up, currentViewport } = useViewport()
  const { activeChip, fetchOnClick, posts, isLoading } = useArticlesContext()
  const { addEvent } = useEventContext()
  const activeTopic = !!activeChip ? topics.items.find(topic => topic.slug === activeChip) : undefined
  const topicsForCarousel = topics.items.filter(topic => topic.linkedFrom.posts.total !== 0)

  useEffect(() => {
    const currentChip = document.querySelector('[class*=teal]')
    if (!currentChip) {
      return
    }
    currentChip.scrollIntoView({ block: 'nearest' })
  }, [activeChip])

  const shouldRenderButton =
    DEFAULT_TOPIC.slug === activeChip || !!(activeTopic && activeTopic.linkedFrom.posts?.total > 9)

  return (
    <Grid className="FilterByTopic">
      {is('xs') ? (
        <>
          <Typography className={classes.title}>Filter by topic:</Typography>

          <div className={classes.filtersXs}>
            <div className={classes.carouselContainer}>
              <TopicsCarousel activeChip={activeChip} fetchOnClick={fetchOnClick} topics={topicsForCarousel} />
            </div>
            <TopicChip
              key={SEE_ALL_TOPIC.slug}
              topic={SEE_ALL_TOPIC}
              size={up('md') ? 'medium' : 'small'}
              className={classes.seeAllChip}
              data-testid="topic-chip-all"
            />
          </div>
        </>
      ) : (
        <>
          <div className={classes.filters}>
            <Typography className={classes.title}>Filter by topic:</Typography>
            <div className={classes.carouselContainer}>
              <TopicsCarousel activeChip={activeChip} fetchOnClick={fetchOnClick} topics={topicsForCarousel} />
            </div>
            <TopicChip
              key={SEE_ALL_TOPIC.slug}
              topic={SEE_ALL_TOPIC}
              size={up('md') ? 'medium' : 'small'}
              className={classes.seeAllChip}
              data-testid="topic-chip-all"
            />
          </div>
        </>
      )}

      <Masonry
        className={classes.masonry}
        columnClassName={classes.masonryColumn}
        breakpointCols={{
          default: MASONRY_COLUMNS[currentViewport] ? MASONRY_COLUMNS[currentViewport] : 3,
          959: 2,
          599: 1,
        }}
      >
        {isLoading
          ? // eslint-disable-next-line react/no-array-index-key
            [...Array(9)].map((_, index) => <ArticleCardSkeleton key={`article-card-${index}`} />)
          : posts.map(post => <ArticleCard className={classes.card} key={post.sys.id} post={post} />)}
      </Masonry>
      <div className={clsx(classes.buttonContainer, { [classes.noButton]: !shouldRenderButton })}>
        {shouldRenderButton && (
          <Button
            className={classes.button}
            href={
              !!activeTopic
                ? routeService.generatePath(ROUTE_NAMES.RESOURCES_TOPIC, { slug: activeChip })
                : routeService.generatePath(ROUTE_NAMES.RESOURCES_ALL)
            }
            variant="outlined"
            {...addEvent({
              engagementComponent: 'resources',
              engagementDestination: !!activeTopic ? 'go-to_topic-articles' : 'go-to_all-articles',
              engagementDetails: !!activeTopic ? `button_${activeTopic.slug}` : 'button_all-articles',
              name: 'engagement_facet_click',
            })}
          >
            {!!activeTopic ? `See all ${activeTopic.name} articles` : 'See all articles'}
          </Button>
        )}
      </div>
    </Grid>
  )
}

export default FilterByTopic
